import {useSelector} from 'umi';
import React, {useEffect} from 'react';
import {Col, ColProps, Row, Space} from 'antd';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import {
  AimOutlined,
  ApiOutlined,
  AppstoreOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CommentOutlined,
  DeliveredProcedureOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  FormOutlined,
  GlobalOutlined,
  KeyOutlined,
  LinkOutlined,
  MailOutlined,
  NotificationOutlined,
  PhoneOutlined,
  ProductOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import {INITIAL_PROPERTY_VISIBILITY_SETTINGS, LEAD_PROPERTIES,} from '@/components/Kanban/VisibilitySettings/constants';

import {LeadModel} from '@/typings/models/Lead';
import * as PreferencesSelectors from '@/selectors/preferencesSelectors';
import {F, INTL_ATTRIBUTE, INTL_ENTITY} from '@/utils/intl';

// region Lead Components
import LeadName from '@/components/Lead/LeadName';
import LeadEmail, { getLeadEmail } from '@/components/Lead/LeadEmail';
import LeadLocale from '@/components/Lead/LeadLocale';
import LeadSource from '@/components/Lead/LeadSource';
import LeadDealSize from '@/components/Lead/LeadDealSize';
import LeadMarketingChannel from '@/components/Lead/LeadMarketingChannel';
import LeadPhone, { getLeadPhone } from '@/components/Lead/LeadPhone';
import LeadAssigneeOverview from '@/components/Lead/LeadAssigneeOverview';
import LeadState from '@/components/Lead/LeadState';
import LeadCreatedAt from '@/components/Lead/LeadCreatedAt';
import LeadUpdatedAt from '@/components/Lead/LeadUpdatedAt';
import LeadAppointmentAt from '@/components/Lead/LeadAppointmentAt';
import LeadPriority from '@/components/Lead/LeadPriority';
import LeadNote from '@/components/Lead/LeadNote';
// endregion
import styles from './index.less';
import classNames from 'classnames';
import {CUSTOMER_PROPERTIES} from '../CustomerDrawer/constants';
import ResultLink from '../CustomerDrawer/ResultLink';
import {LEAD_SOURCE_TYPE_JOURNEY} from '@/constants/leadsourceTypes';
import * as ExtraFieldsSelectors from '@/selectors/extraFieldsSelectors';
import {ExtraFieldItem} from '@/components/ExtraFields/ExtraFieldDynamicForm';
import LeadMarketingSource from '@/components/Lead/LeadMarketingSource';
import {useHasMarketingDataAccess} from '@/utils/hooks';
import {isMarketingDataViewField} from '@/utils/marketingDataView';
import LeadProductGroup from '@/components/Lead/LeadProductGroup';
import LeadTitle from '@/components/Lead/LeadTitle';
import LeadCustomerIntegrations from '@/components/Lead/LeadCustomerIntegrations';
import LeadUuid from '@/components/Lead/LeadUuid';
import LeadMarketingCampaign from '@/components/Lead/LeadMarketingCampaign';
import LeadCustomerCreatedAt from '@/components/Lead/LeadCustomerCreatedAt';
import LeadCustomerModifiedAt from '@/components/Lead/LeadCustomerModifiedAt';
import LeadAppointmentBranch from '@/components/Lead/LeadAppointmentBranch';
import LeadAppointmentBranchService from '@/components/Lead/LeadAppointmentBranchService';
import LeadAppointmentServiceType from '@/components/Lead/LeadAppointmentServiceType';
import {EuroSymbolIcon} from '@/components/Icons/CustomReactIcons';
import { getCustomerCompany } from '@/utils/contacts';

const LEAD_CONVERSIONAL_JOURNEY_ONLY_PROPS =[
  LEAD_PROPERTIES.RESULT,
];
const LEAD_PROPERTY_ICONS = {
  [LEAD_PROPERTIES.UUID]: <KeyOutlined />,
  [LEAD_PROPERTIES.NAME]: null,
  [LEAD_PROPERTIES.EMAIL]: <MailOutlined />,
  [LEAD_PROPERTIES.LOCALE]: <GlobalOutlined />,
  [LEAD_PROPERTIES.ASSIGNEE]: <TeamOutlined />,
  [LEAD_PROPERTIES.COMMENTS]: <CommentOutlined />,
  [LEAD_PROPERTIES.CREATED_AT]: <CalendarOutlined />,
  [LEAD_PROPERTIES.UPDATED_AT]: <ClockCircleOutlined />,
  [LEAD_PROPERTIES.APPOINTMENT_AT]: <CalendarOutlined />,
  [LEAD_PROPERTIES.DEAL_SIZE]: <EuroSymbolIcon />,
  [LEAD_PROPERTIES.PHONE]: <PhoneOutlined />,
  [LEAD_PROPERTIES.MARKETING_CHANNEL]: <GlobalOutlined />,
  [LEAD_PROPERTIES.MARKETING_SOURCE]: <GlobalOutlined />,
  [LEAD_PROPERTIES.MARKETING_CAMPAIGN]: <NotificationOutlined />,
  [LEAD_PROPERTIES.CUSTOMER_CREATED_AT]: <CalendarOutlined />,
  [LEAD_PROPERTIES.CUSTOMER_MODIFIED_AT]: <ClockCircleOutlined />,
  [LEAD_PROPERTIES.LATEST_APPOINTMENT_BRANCH]: <EnvironmentOutlined />,
  [LEAD_PROPERTIES.LATEST_APPOINTMENT_BRANCH_SERVICE]: <AppstoreOutlined />,
  [LEAD_PROPERTIES.LATEST_APPOINTMENT_SERVICE_TYPE]: <ProductOutlined />,
  [LEAD_PROPERTIES.PRODUCT_GROUP]: <ProductOutlined />,
  [LEAD_PROPERTIES.SOURCE]: <DeliveredProcedureOutlined />,
  [LEAD_PROPERTIES.NOTE]: <FormOutlined />,
  [LEAD_PROPERTIES.TITLE]: <FileTextOutlined />,
  [LEAD_PROPERTIES.CALL_TRACKING]: <AimOutlined />,
  [LEAD_PROPERTIES.RESULT]:  <LinkOutlined />,
  [LEAD_PROPERTIES.CUSTOMER_INTEGRATIONS]: <ApiOutlined />,


};

const LEAD_PROPERTY_COMPONENTS = {
  [LEAD_PROPERTIES.UUID]: LeadUuid,
  [LEAD_PROPERTIES.NAME]: LeadName,
  [LEAD_PROPERTIES.EMAIL]: LeadEmail,
  [LEAD_PROPERTIES.LOCALE]: LeadLocale,
  [LEAD_PROPERTIES.PHONE]: LeadPhone,
  [LEAD_PROPERTIES.MARKETING_CHANNEL]: LeadMarketingChannel,
  [LEAD_PROPERTIES.MARKETING_SOURCE]: LeadMarketingSource,
  [LEAD_PROPERTIES.MARKETING_CAMPAIGN]: LeadMarketingCampaign,
  [LEAD_PROPERTIES.CUSTOMER_CREATED_AT]: LeadCustomerCreatedAt,
  [LEAD_PROPERTIES.CUSTOMER_MODIFIED_AT]: LeadCustomerModifiedAt,
  [LEAD_PROPERTIES.LATEST_APPOINTMENT_BRANCH]: LeadAppointmentBranch,
  [LEAD_PROPERTIES.LATEST_APPOINTMENT_BRANCH_SERVICE]: LeadAppointmentBranchService,
  [LEAD_PROPERTIES.LATEST_APPOINTMENT_SERVICE_TYPE]: LeadAppointmentServiceType,
  [LEAD_PROPERTIES.PRODUCT_GROUP]: LeadProductGroup,
  [LEAD_PROPERTIES.DEAL_SIZE]: LeadDealSize,
  [LEAD_PROPERTIES.CREATED_AT]: LeadCreatedAt,
  [LEAD_PROPERTIES.UPDATED_AT]: LeadUpdatedAt,
  [LEAD_PROPERTIES.APPOINTMENT_AT]: LeadAppointmentAt,
  [LEAD_PROPERTIES.ASSIGNEE]: LeadAssigneeOverview,
  [LEAD_PROPERTIES.STATUS]: LeadState,
  [LEAD_PROPERTIES.PRIORITY]: LeadPriority,
  [LEAD_PROPERTIES.SOURCE]: LeadSource,
  [LEAD_PROPERTIES.NOTE]: LeadNote,
  [LEAD_PROPERTIES.TITLE]: LeadTitle,
  [LEAD_PROPERTIES.CUSTOMER_INTEGRATIONS]: LeadCustomerIntegrations,
  [CUSTOMER_PROPERTIES.RESULT]: ResultLink,
};

const LEAD_PROPERTY_VALIDATORS = {
  [LEAD_PROPERTIES.UUID]: (lead: LeadModel) => get(lead, 'id'),
  [LEAD_PROPERTIES.NAME]: (lead: LeadModel) =>
    getCustomerCompany(lead?.customer) || get(lead, 'customer.firstname') || get(lead, 'customer.lastname'),
  [LEAD_PROPERTIES.EMAIL]: (lead: LeadModel) => getLeadEmail(lead),
  [LEAD_PROPERTIES.LOCALE]: (lead: LeadModel) => get(lead, 'customer.locale'),
  [LEAD_PROPERTIES.PHONE]: (lead: LeadModel) => getLeadPhone(lead),
  [LEAD_PROPERTIES.MARKETING_CHANNEL]: (lead: LeadModel) => get(lead, 'customer.utm_medium'),
  [LEAD_PROPERTIES.MARKETING_SOURCE]: (lead: LeadModel) => get(lead, 'customer.utm_source'),
  [LEAD_PROPERTIES.MARKETING_CAMPAIGN]: (lead: LeadModel) => get(lead, 'customer.utm_campaign'),
  [LEAD_PROPERTIES.CUSTOMER_CREATED_AT]: (lead: LeadModel) => get(lead, 'customer.created'),
  [LEAD_PROPERTIES.CUSTOMER_MODIFIED_AT]: (lead: LeadModel) => get(lead, 'customer.modified'),
  [LEAD_PROPERTIES.LATEST_APPOINTMENT_BRANCH]: (lead: LeadModel) => get(lead, 'latest_appointment.branch_service.branch'),
  [LEAD_PROPERTIES.LATEST_APPOINTMENT_BRANCH_SERVICE]: (lead: LeadModel) => get(lead, 'latest_appointment.branch_service.name'),
  [LEAD_PROPERTIES.LATEST_APPOINTMENT_SERVICE_TYPE]: (lead: LeadModel) => get(lead, 'latest_appointment.branch_service.service_type.name'),
  [LEAD_PROPERTIES.DEAL_SIZE]: (lead: LeadModel) => get(lead, 'order_volume'),
  [LEAD_PROPERTIES.CREATED_AT]: () => true,
  [LEAD_PROPERTIES.UPDATED_AT]: () => true,
  [LEAD_PROPERTIES.APPOINTMENT_AT]: () => true,
  [LEAD_PROPERTIES.ASSIGNEE]: (lead: LeadModel) => !isEmpty(lead?.assignee),
  [LEAD_PROPERTIES.STATUS]: (lead: LeadModel) => !isEmpty(lead?.lead_state),
  [LEAD_PROPERTIES.PRIORITY]: (lead: LeadModel) => !isEmpty(lead?.priority),
  [LEAD_PROPERTIES.SOURCE]: (lead: LeadModel) => !isEmpty(lead?.lead_source),
  [LEAD_PROPERTIES.NOTE]: (lead: LeadModel) => get(lead, 'customer.notes'),
  [LEAD_PROPERTIES.TITLE]: (lead: LeadModel) => get(lead, 'title'),
  [LEAD_PROPERTIES.PRODUCT_GROUP]: (lead: LeadModel) => get(lead, 'product_group.id'),
  [LEAD_PROPERTIES.CUSTOMER_INTEGRATIONS]: (lead: LeadModel) => get(lead, 'customer.integrations.length'),
};

export const validateLeadField = (lead, property) => {
  const validator = LEAD_PROPERTY_VALIDATORS[property];
  if (!validator) return true;
  return validator(lead);
};

export const LEAD_PROPERTY_COLUMN_SETTINGS = {
  [LEAD_PROPERTIES.UUID]: {
    title: {
      id: 'pages.lead.fields.uuid',
      defaultMessage: 'ID',
    },
  },
  [LEAD_PROPERTIES.NAME]: {
    title: {
      id: 'pages.lead.fields.name',
      defaultMessage: 'Name',
    },
    extraProps: {
      fixed: 'left',
    },
  },
  [LEAD_PROPERTIES.EMAIL]: {
    title: {
      id: 'pages.lead.fields.email',
      defaultMessage: 'Email',
    },
  },
  [LEAD_PROPERTIES.LOCALE]: {
    title: {
      id: 'pages.lead.fields.locale',
      defaultMessage: 'Language',
    },
  },
  [LEAD_PROPERTIES.ASSIGNEE]: {
    title: {
      id: 'pages.lead.fields.assignee',
      defaultMessage: 'Assignee',
    },
  },
  [LEAD_PROPERTIES.CREATED_AT]: {
    title: {
      id: 'pages.lead.fields.createdAt',
      defaultMessage: 'Create date',
    },
  },
  [LEAD_PROPERTIES.UPDATED_AT]: {
    title: {
      id: 'pages.lead.fields.updatedAt',
      defaultMessage: 'Update date',
    },
  },
  [LEAD_PROPERTIES.APPOINTMENT_AT]: {
    title: {
      id: 'pages.lead.fields.appointmentAt',
      defaultMessage: 'Appointment date',
    },
  },
  [LEAD_PROPERTIES.DEAL_SIZE]: {
    title: {
      id: 'pages.lead.fields.dealSize',
      defaultMessage: 'Deal size',
    },
  },
  [LEAD_PROPERTIES.LEAD_SOURCE]: {
    title: {
      id: 'pages.lead.fields.leadSource',
      defaultMessage: 'Lead Source',
    },
  },
  [LEAD_PROPERTIES.PHONE]: {
    title: {
      id: 'common.attribute.phone',
      defaultMessage: 'Phone',
    },
  },
  [LEAD_PROPERTIES.MARKETING_CHANNEL]: {
    title: {
      id: 'pages.lead.fields.marketingChannel',
      defaultMessage: 'Channel',
    },
  },
  [LEAD_PROPERTIES.MARKETING_SOURCE]: {
    title: {
      id: 'pages.lead.fields.marketingSource',
      defaultMessage: 'Marketing Source',
    },
  },
  [LEAD_PROPERTIES.MARKETING_CAMPAIGN]: {
    title: {
      id: 'pages.lead.fields.marketingCampaign',
      defaultMessage: 'Marketing Campaign',
    },
  },
  [LEAD_PROPERTIES.CUSTOMER_CREATED_AT]: {
    title: {
      entity: INTL_ENTITY.CUSTOMER,
      attribute: INTL_ATTRIBUTE.CREATED_AT,
    },
  },
  [LEAD_PROPERTIES.CUSTOMER_MODIFIED_AT]: {
    title: {
      entity: INTL_ENTITY.CUSTOMER,
      attribute: INTL_ATTRIBUTE.UPDATED_AT,
    },
  },
  [LEAD_PROPERTIES.LATEST_APPOINTMENT_BRANCH]: {
    title: {
      id: 'pages.lead.fields.appointmentBranch',
      defaultMessage: 'Appointment Branch',
    },
  },
  [LEAD_PROPERTIES.LATEST_APPOINTMENT_BRANCH_SERVICE]: {
    title: {
      id: 'pages.lead.fields.appointmentBranchService',
      defaultMessage: 'Branch Service',
    },
  },
  [LEAD_PROPERTIES.LATEST_APPOINTMENT_SERVICE_TYPE]: {
    title: {
      id: 'pages.lead.fields.appointmentServiceType',
      defaultMessage: 'Service Type',
    },
  },
  [LEAD_PROPERTIES.STATUS]: {
    title: {
      id: 'pages.lead.fields.status',
      defaultMessage: 'Status',
    },
  },
  [LEAD_PROPERTIES.PRIORITY]: {
    title: {
      id: 'pages.lead.fields.priority',
      defaultMessage: 'Priority',
    },
  },
  [LEAD_PROPERTIES.NOTE]: {
    title: {
      id: 'pages.lead.fields.note',
      defaultMessage: 'Notes',
    },
  },
  [LEAD_PROPERTIES.TITLE]: {
    title: {
      id: 'common.attribute.title',
      defaultMessage: 'Title',
    },
  },
  [LEAD_PROPERTIES.PRODUCT_GROUP]: {
    title: {
      id: 'entity.product_group.singular',
      defaultMessage: 'Product Group',
    },
  },
};

type ILeadPropertyComponentProps = {
  lead: LeadModel;
  property: string;
  isTable?: boolean;
  showOnePhone?: boolean;
  showIcon?: boolean;
  readonly?: boolean;
};
export const LeadPropertyComponent = ({
  lead,
  property,
  isTable,
  showOnePhone = false,
  showIcon = false,
  readonly,
  ...propertyProps
}: ILeadPropertyComponentProps) => {
  if (!isTable && [LEAD_PROPERTIES.STATUS].includes(property)) return null;
  const Component = LEAD_PROPERTY_COMPONENTS[property];
  if (!Component) return null;

  const Icon = LEAD_PROPERTY_ICONS[property];
  const Presentation = (
    <Component
      lead={lead}
      isTable={isTable}
      showOnePhone={showOnePhone}
      readonly={readonly}
      {...propertyProps}
    />
  );
  if (showIcon){
    return <Space>
      {Icon}
      {Presentation}
    </Space>
  }
  // @ts-ignore
  return Presentation;
};


type IOrderedLeadPropertiesType = {
  lead: LeadModel;
  showAllProperties?: boolean;
  showFieldName?: boolean;
  showOnePhone?: boolean;
  isTable?: boolean;
  spaceSize?: number;
  grid?: ColProps;
  overrideVisibleProperties?: string[];
  onVisiblePropertiesChange?: () => void;
  classes?: {
    fieldName?: string;
    fieldValue?: string;
  };
  propertyProps?: {
    [key: string]: any
  }
};
export const LeadCardOrderedProperties = React.memo(
  ({
    lead,
    showAllProperties,
    showFieldName = false,
    isTable = false,
    isCard = false,
    spaceSize = 7,
    grid,
    onVisiblePropertiesChange,
    propertyProps = {},
    overrideVisibleProperties = [],
    classes = {},
    showOnePhone = false,
  }: Readonly<IOrderedLeadPropertiesType>) => {

    const [isInitialized, setIsInitialized] = React.useState(false);
    const extraFieldTypes = useSelector(ExtraFieldsSelectors.selectExtraFieldTypesList)
    const hasMarketingDataAccess = useHasMarketingDataAccess()

    let visibleProperties = useSelector(
      showAllProperties
        ? PreferencesSelectors.selectAllLeadsProperties
        : PreferencesSelectors.selectShownLeadsProperties,
    );
    if (isEmpty(visibleProperties)) visibleProperties = INITIAL_PROPERTY_VISIBILITY_SETTINGS.shown;

    useEffect(() => {
      if (!isInitialized) return setIsInitialized(true);

      if (onVisiblePropertiesChange) onVisiblePropertiesChange();
    }, [JSON.stringify(visibleProperties)]);

    const getVisibleProperties = () =>
      isEmpty(overrideVisibleProperties) ? visibleProperties : overrideVisibleProperties;
    const presentations = getVisibleProperties()
      .map((property) => {
        if (
          LEAD_CONVERSIONAL_JOURNEY_ONLY_PROPS.includes(property) &&
          lead?.lead_source?.type !== LEAD_SOURCE_TYPE_JOURNEY
        )
          return null;

        if (!hasMarketingDataAccess && isMarketingDataViewField(property)) {
          return null;
        }

        if (property.includes('extraField:')) {
          const extraFieldType = extraFieldTypes.find(fieldType => fieldType.uuid === property.split(':')[1]);
          if (!extraFieldType) return null;
          return <div className={styles.inlineExtraFieldForm}><ExtraFieldItem isInline field={extraFieldType} leadId={lead?.id}/></div>
        }
        const Presentation = LeadPropertyComponent({ lead, property, isTable, isCard, showOnePhone, ...(propertyProps[property] || {}) });
        if (!Presentation) return null;
        const Icon = LEAD_PROPERTY_ICONS[property];
        // const Icon = LEAD_PROPERTY_ICONS[property];
        const fieldWithIcon = (
          <div key={property} className={styles.propertyRow}>
            {Icon && (
              <div className={styles.propertyIcon} onClick={(e) => e.stopPropagation()}>
                {Icon}
              </div>
            )}
            {Presentation && (
              <div
                onClick={(e) => e.stopPropagation()}
                className={classNames(styles.propertyPresentation, classes.fieldValue)}
              >
                {Presentation}
              </div>
            )}
          </div>
        );
        if (!showFieldName) return fieldWithIcon;
        return (
          <Space
            onClick={(e) => e.stopPropagation()}
            direction={'vertical'}
            size={4}
            key={`${lead?.id}_${property}`}
          >
            <strong className={classes.fieldName}>
              <F
                id={`pages.lead.fields.${property}`}
                defaultMessage={
                  LEAD_PROPERTY_COLUMN_SETTINGS[property]?.title && (
                    <F {...LEAD_PROPERTY_COLUMN_SETTINGS[property].title} />
                  )
                }
              />
            </strong>
            {fieldWithIcon}
          </Space>
        );
      })
      .filter((property) => property);
    let content = presentations;

    if (grid) {
      content = (
        <Row gutter={[spaceSize, spaceSize]}>
          {presentations.map((presentation, index) => (
            <Col {...grid} key={index}>
              {presentation}
            </Col>
          ))}
        </Row>
      );
    }
    return (
      <div
      className={styles.propertiesContainer} style={{ gap: spaceSize }}>
        {content}
      </div>
    );
  },
  (prevProps, nextProps) => {
    return (
      isEqual(prevProps.lead, nextProps.lead) &&
      isEqual(prevProps.overrideVisibleProperties, nextProps.overrideVisibleProperties)
    );
  },
);
